import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import WrapperCard from "../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../component-lib/Atoms/heading/EventEHRHeading";
import EventEHRTabs from "../../component-lib/Molecules/tabs/EventEHRTabs";
import SelectDropdown from "../../component-lib/Atoms/Select/Select";
import useDeviceType from "../../custom-hooks/DeviceType";
import useHasOverflow from "../../custom-hooks/HasOverflow";
import FacilityManagementService from "../../services/FacilityManagementService";
import { ALL_FACILITIES_OPTION } from "./ReportConstants";
import InventoryReportTab from "./tabs/InventoryReportTab";
import IncidentReportTab from "./tabs/IncidentReportTab";
import UsageReportTab from "./tabs/UsageReportTab";
import DailyOrdersReportTab from "./tabs/DailyOrdersReportTab";
import EmsDailyReportTab from "./tabs/EmsDailyReportTab";
import FacilityPatientHistoryTab from "./tabs/FacilityPatientHistoryTab";
import IcdReportTab from "./tabs/IcdReportTab";
import ProviderPatientLogTab from "./tabs/ProviderPatientLogTab";

// Ensure the reports redux slice + saga are registered
import "./ReportsSaga";

const REPORT_TABS = [
  { label: "Inventory", value: "inventory" },
  { label: "Incident Locations", value: "incident" },
  { label: "Usage", value: "usage" },
  { label: "Daily Orders & Medications", value: "dailyOrders" },
  { label: "EMS Daily Visits", value: "emsDaily" },
  { label: "Facility Patient History", value: "facilityPatientHistory" },
  { label: "ICD/DSM Codes", value: "icd" },
  { label: "Provider Patient Log", value: "providerPatientLog" },
];

const ReportsScreen = () => {
  const deviceType = useDeviceType();
  const divRef = useRef<HTMLDivElement>(null);
  const hasOverflow = useHasOverflow(divRef);

  // Allow deep-linking to a specific report tab (e.g. the dashboard ICD card's
  // "View more" → /reports?tab=icd). Falls back to Inventory for no/invalid tab.
  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(() => {
    const requestedTab = searchParams.get("tab");
    return REPORT_TABS.some((tab) => tab.value === requestedTab)
      ? (requestedTab as string)
      : "inventory";
  });
  const [facilityId, setFacilityId] = useState("");
  const [facilityOptions, setFacilityOptions] = useState<
    { label: string; value: string }[]
  >([ALL_FACILITIES_OPTION]);

  // Load the facility list once for the scope selector (super admin = all facilities)
  useEffect(() => {
    const loadFacilities = async () => {
      try {
        const response = await FacilityManagementService.getFacilityList({
          pageNumber: 1,
          limit: 100,
        });
        const facilities: Array<{ id: string; name: string }> =
          response?.data?.facilitys || [];
        setFacilityOptions([
          ALL_FACILITIES_OPTION,
          ...facilities.map((facility) => ({
            label: facility?.name,
            value: facility?.id,
          })),
        ]);
      } catch {
        setFacilityOptions([ALL_FACILITIES_OPTION]);
      }
    };
    loadFacilities();
  }, []);

  return (
    <WrapperCard
      customClasses="m-3 p-3 pl-4 pr-4 flex-1 min-h-0 flex flex-col"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col flex-1 min-h-0 gap-3">
        {/* SCREEN HEADER */}
        <div className="flex items-center justify-between w-full gap-3 flex-wrap">
          <Heading title="Reports" />
          {/* The Facility Patient History tab has its own dedicated "Seen at
              Facility" / "Current Facility" filters, so the shared scope
              selector is hidden there to avoid three overlapping facility
              controls. */}
          {selectedTab !== "facilityPatientHistory" && (
            <div style={{ minWidth: "240px" }}>
              <SelectDropdown
                label="Facility"
                value={facilityId}
                options={facilityOptions}
                onChangeCb={(e) => setFacilityId(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* MAIN CONTENT */}
        <div
          style={{
            overflow: "auto",
            flex: "1 1 0%",
            minHeight: 0,
            paddingRight: hasOverflow ? "7px" : "0",
          }}
          ref={divRef}
        >
          <EventEHRTabs
            defaultSelectedValue={selectedTab}
            tabList={REPORT_TABS}
            onChangeTab={(tab) => setSelectedTab(tab)}
          />

          {selectedTab === "inventory" && (
            <InventoryReportTab facilityId={facilityId} />
          )}
          {selectedTab === "incident" && (
            <IncidentReportTab facilityId={facilityId} />
          )}
          {selectedTab === "usage" && (
            <UsageReportTab facilityId={facilityId} />
          )}
          {selectedTab === "dailyOrders" && (
            <DailyOrdersReportTab facilityId={facilityId} />
          )}
          {selectedTab === "emsDaily" && (
            <EmsDailyReportTab facilityId={facilityId} />
          )}
          {selectedTab === "facilityPatientHistory" && (
            // Scope comes from this tab's own facility filters, not the shared
            // selector (which is hidden here), so never inherit a global value.
            <FacilityPatientHistoryTab facilityId="" />
          )}
          {selectedTab === "icd" && <IcdReportTab facilityId={facilityId} />}
          {selectedTab === "providerPatientLog" && (
            <ProviderPatientLogTab facilityId={facilityId} />
          )}
        </div>
      </div>
    </WrapperCard>
  );
};

export default ReportsScreen;
